import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Building, 
  Home,
  ChevronDown,
  AlertCircle,
  ClipboardList,
  ClipboardCheck,
  CheckCircle,
  Info
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../apiConfig/api';
import TradeLicenseSection from './tradelisencesection';
import { useSurvey } from '../context/surveycontext';
import ResponsivePropertyTable from '../components/propertytable';

// SelectField component remains exactly the same
const SelectField = ({ label, id, options, error, icon: Icon, className = '', ...props }) => (
  <div className={`space-y-2 relative group ${className}`}>
    <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-1.5">
      {label}
    </label>
    <div className="relative">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-[#75d1e3] group-hover:text-[#5dbdd0] transition-colors" />
        </div>
      )}
      <select
        id={id}
        className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-10 py-3.5 bg-white border-2 border-gray-200 
        rounded-xl focus:ring-2 focus:ring-[#75d1e3] focus:border-[#75d1e3] transition-all duration-200 
        hover:border-[#75d1e3] shadow-sm appearance-none text-black font-medium 
        disabled:bg-gray-50 disabled:cursor-not-allowed`}
        {...props}
      >
        <option value="" className="text-gray-500">Select {label}</option>
        {options?.map((option, index) => (
          <option 
            key={`${option.value}-${index}`} 
            value={option.value} 
            className="text-black font-medium"
          >
            {option.label}
          </option>
        ))}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <ChevronDown className="h-5 w-5 text-gray-400" />
      </div>
    </div>
    {error && (
      <div className="absolute -bottom-6 left-0 flex items-center gap-2 text-red-500 text-sm">
        <AlertCircle size={14} />
        <span className="font-medium">{error}</span>
      </div>
    )}
  </div>
);

// Tab component remains exactly the same
const Tab = ({ active, icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-6 py-4 font-medium transition-all duration-200 border-b-2 ${
      active 
        ? 'text-[#75d1e3] border-[#75d1e3]' 
        : 'text-gray-500 border-transparent hover:text-[#75d1e3] hover:border-[#75d1e3]'
    }`}
  >
    <Icon className="h-5 w-5" />
    {label}
  </button>
);

const SurveyForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { zoneName: contextZoneName } = useSurvey();
  const assessmentInputRef = useRef(null);
  
  const routeParams = {
    orgId: location.state?.orgId,
    userId: location.state?.userId,
    zoneId: location.state?.zoneId,
    organizationName: location.state?.organizationName,
    zoneName: location.state?.zoneName || contextZoneName,
    orgCode: location.state?.orgCode
  };
  
  // All state declarations
  const [activeTab, setActiveTab] = useState('pending');
  const [assessmentId, setAssessmentId] = useState('');
  const [assessmentSearchLoading, setAssessmentSearchLoading] = useState(false);
  const [formData, setFormData] = useState({
    ward_id: '',
    street_id: ''
  });
  const [wardStreetSearchLoading, setWardStreetSearchLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [userWards, setUserWards] = useState([]);
  const [userStreets, setUserStreets] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [wardAssessments, setWardAssessments] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [orgName, setOrgName] = useState(routeParams.organizationName || '');
  const [streetLoading, setStreetLoading] = useState(false);
  const [streetError, setStreetError] = useState('');
  const [completedData, setCompletedData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [completedDataLoading, setCompletedDataLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [assessmentSearchError, setAssessmentSearchError] = useState('');
  const [shouldAutoSearch, setShouldAutoSearch] = useState(false);
  const [searchMethod, setSearchMethod] = useState(null);
  
  const [wardsLoading, setWardsLoading] = useState(false);
  const [wardsError, setWardsError] = useState('');
  const [orgCode, setOrgCode] = useState(localStorage.getItem('org_code') || '');
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [zoneName, setZoneName] = useState('');
  const [streetData, setStreetData] = useState({});
  const [completedAssessments, setCompletedAssessments] = useState({});
  const [isRestoringSession, setIsRestoringSession] = useState(false);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowAutocomplete(false);
      }
    }
  
    // Attach the event listener
    document.addEventListener("mousedown", handleClickOutside);
    
    // Clean up on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  useEffect(() => {
    const storedZoneName = localStorage.getItem('zone_name');
    setZoneName(storedZoneName || 'No Zone');
  }, []);
  
  // Main session management logic
  useEffect(() => {
    const isReturningFromVerification = () => {
      const fromVerification = sessionStorage.getItem('fromVerification') === 'true';
      const lastPath = sessionStorage.getItem('lastPath') || '';
      
      // True if the fromVerification flag is set to true and the last path contains verify
      return fromVerification && lastPath.includes('/verify');
    };
    
    const fetchWards = async () => {
      setWardsLoading(true);
      setWardsError('');
  
      try {
        const wardDetailsStr = localStorage.getItem('ward_details');
        if (wardDetailsStr) {
          const wardDetails = JSON.parse(wardDetailsStr);
          const formattedWards = wardDetails.map(ward => ({
            value: ward.wardId.toString(),
            label: ward.wardName
          }));
          setUserWards(formattedWards);
  
          // Check if we just came from verification
          const storedFormData = localStorage.getItem('formData');
          const returningFromVerification = isReturningFromVerification();
          
          // Store current path for next navigation check
          sessionStorage.setItem('lastPath', location.pathname);
          
          // Only populate form if returning from verification
          if (storedFormData && returningFromVerification) {
            setIsRestoringSession(true);
            
            try {
              const parsedFormData = JSON.parse(storedFormData);
              
              // Set form data from storage
              setFormData({
                ward_id: parsedFormData.ward_id,
                street_id: parsedFormData.street_id
              });
              
              // Set the search method we were using
              const previousSearchMethod = sessionStorage.getItem('searchMethod');
              if (previousSearchMethod) {
                setSearchMethod(previousSearchMethod);
              }
              
              // Load streets first, then trigger auto-search
              if (parsedFormData.ward_id) {
                await loadStreets(parsedFormData.ward_id);
              }
              
              setHasSearched(true);
              setShouldAutoSearch(true);
            } catch (error) {
              console.error('Error restoring session:', error);
            } finally {
              setIsRestoringSession(false);
            }
          }
          
          // Clear verification flags if not returning from verification
          if (!returningFromVerification) {
            clearStoredData();
          }
        } else {
          setWardsError('No ward data available');
          setUserWards([]);
        }
      } catch (error) {
        console.error('Error fetching wards:', error);
        setWardsError('Failed to load wards');
        setUserWards([]);
      } finally {
        setWardsLoading(false);
      }
    };
  
    fetchWards();
  
    // Cleanup function
    return () => {
      // Check if we're navigating to verification or somewhere else
      if (!location.pathname.includes('/verify')) {
        clearStoredData();
      }
    };
  }, [location.pathname]);

  // Load streets for a given ward
  const loadStreets = async (wardId) => {
    if (!wardId) return;
    
    setStreetLoading(true);
    setStreetError('');
    
    try {
      const response = await api.get(`/asset?ward_id=${wardId}`);
      
      if (response.data?.data) {
        // Format streets for dropdown using the new response format
        const streets = response.data.data.map(street => ({
          value: street.street_id,
          label: street.street_name
        }));
        setUserStreets(streets);
      } else {
        setStreetError('No streets found');
        setUserStreets([]);
      }
      
      return true;
    } catch (error) {
      console.error('Error fetching streets:', error);
      setStreetError('Failed to load streets');
      setUserStreets([]);
      return false;
    } finally {
      setStreetLoading(false);
      setIsInitialLoading(false);
    }
  };
  // Auto-search effect after restoring session
  useEffect(() => {
    const autoSearch = async () => {
      if (shouldAutoSearch && formData.ward_id && formData.street_id && !isRestoringSession) {
        processStreetData(formData.street_id);
        setShouldAutoSearch(false);
      }
    };
  
    autoSearch();
  }, [formData.ward_id, formData.street_id, shouldAutoSearch, isRestoringSession]);

  // Set initial assessment ID format with org code
  useEffect(() => {
    const storedOrgCode = localStorage.getItem('organization_code');
    if (storedOrgCode) {
      setOrgCode(storedOrgCode);
      setAssessmentId(`${storedOrgCode}/`);
    }
  }, []); 

  const clearStoredData = () => {
    // Keep formData in localStorage to use between navigations
    // but clear the session flags
    sessionStorage.removeItem('fromVerification');
    sessionStorage.removeItem('searchMethod');
    sessionStorage.removeItem('pendingStreetId');
  };

  // Method to store session before navigating to verification
  const storeSessionAndNavigate = (assessmentNo, isCompleted = false, reportId = null) => {
    // Save current form state
    localStorage.setItem('formData', JSON.stringify({
      ward_id: formData.ward_id,
      street_id: formData.street_id
    }));
    
    // Set flag to indicate where we're coming from
    sessionStorage.setItem('fromVerification', 'true');
    sessionStorage.setItem('searchMethod', searchMethod || 'ward-street');
    
    // Navigate directly using the assessment number in the URL
    if (isCompleted) {
      navigate(`/report/${encodeURIComponent(reportId || assessmentNo)}`);
    } else {
      navigate(`/verify/${encodeURIComponent(assessmentNo)}`, {
        state: {
          orgid: routeParams.orgId,
          wid: formData.ward_id,
          // No need to pass asstid as we're using the assessment number in URL
          assessmentNo: assessmentNo
        }
      });
    }
  };
  
  
// Function to fetch autocomplete results as user types
const fetchAutocompleteResults = async (searchText) => {
  if (!formData.ward_id || searchText.length <= 7) return;
  
  setAutocompleteLoading(true);
  try {
    // Call the search API with the partial assessment ID to get matches
    const response = await api.get(`/asset/search`, {
      params: { asst_ref: searchText }
    });
    
    if (response.data?.data && Array.isArray(response.data.data)) {
      // Format the results for display
      const formattedResults = response.data.data;
      setAutocompleteResults(formattedResults);
      setShowAutocomplete(formattedResults.length > 0);
    } else {
      setAutocompleteResults([]);
      setShowAutocomplete(false);
    }
  } catch (error) {
    console.error('Error fetching autocomplete results:', error);
    setAutocompleteResults([]);
    setShowAutocomplete(false);
  } finally {
    setAutocompleteLoading(false);
  }
};

  const formatAssessmentId = (value) => {
    const numbers = value.replace(/[^\d]/g, '');
    
    if (numbers.length <= 3) {
      return `${orgCode}/`;
    }
  
    const postOrgCode = numbers.slice(3);
    
    // Add the forward slash immediately after the second group of 3 digits
    if (postOrgCode.length === 3) {
      return `${orgCode}/${postOrgCode}/`;
    }
    
    if (postOrgCode.length <= 3) {
      return `${orgCode}/${postOrgCode}`;
    }
    
    return `${orgCode}/${postOrgCode.slice(0, 3)}/${postOrgCode.slice(3)}`;
  };

  const handleAssessmentIdChange = (e) => {
    const input = e.target.value;
    const currentParts = assessmentId.split('/');
    const orgPrefix = currentParts[0];
    const wardNumber = currentParts[1] || '';
    
    // Don't allow deleting organization code or ward number parts
    if (input.length < assessmentId.length) {
      // If backspacing beyond the first two parts, maintain them
      if (input.length <= orgPrefix.length + wardNumber.length + 2) { // +2 for the slashes
        setAssessmentId(`${orgPrefix}/${wardNumber}/`);
      } else {
        // Allow deleting in the last part only
        setAssessmentId(input);
      }
      setShowAutocomplete(false);
      return;
    }
    
    // Allow typing only in the last part (after org/ward/)
    const baseLength = orgPrefix.length + wardNumber.length + 2; // +2 for slashes
    const lastPart = input.slice(baseLength).replace(/[^\d]/g, '');
    
    if (lastPart.length <= 6) {
      const newAssessmentId = `${orgPrefix}/${wardNumber}/${lastPart}`;
      setAssessmentId(newAssessmentId);
      
      // Only show autocomplete if we have at least 1 digit in the last part
      if (lastPart.length > 0) {
        fetchAutocompleteResults(newAssessmentId);
      } else {
        setShowAutocomplete(false);
      }
      
      // Auto-search when the last part is complete (6 digits)
      if (lastPart.length === 6) {
        handleAssessmentSearch({ preventDefault: () => {} });
      }
    }
  };

  const clearForm = () => {
    setFormData({ ward_id: '', street_id: '' });
    setSearchResults([]);
    setCompletedData([]);
    setAllData([]);
    setHasSearched(false);
    setActiveTab('pending');
    setAssessmentId(orgCode ? `${orgCode}/` : '');
    setAssessmentSearchError('');
    setSearchError('');
    setAutocompleteResults([]);
    setShowAutocomplete(false);
    localStorage.removeItem('formData');
    sessionStorage.removeItem('fromVerification');
    sessionStorage.removeItem('searchMethod');
    sessionStorage.removeItem('pendingStreetId');
    setShouldAutoSearch(false);
    setSearchMethod(null);
  };

  const openAssessmentByStatus = (assessmentNo, responseData) => {
    const record = Array.isArray(responseData) ? responseData[0] : responseData;
    if (!record) return false;

    const status = String(record.status || '').trim().toLowerCase();
    const isCompleted = status === 'completed' || status === 'complete' ||
      String(record.completed || '').trim().toLowerCase() === 'yes';
    const listedRecord = allData.find(item => item.asst_ref === assessmentNo);
    const reportId = record.survey_id || record.asst_survey_id || record.id || listedRecord?.id;

    if (isCompleted && !reportId) {
      setAssessmentSearchError('Completed survey ID is not available for this assessment');
      return false;
    }

    storeSessionAndNavigate(assessmentNo, isCompleted, reportId);
    return true;
  };

  const handleAssessmentSearch = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    const parts = assessmentId.split('/');
    if (!assessmentId.trim() || parts.length !== 3 || 
        parts[0].length !== 3 || parts[1].length !== 3 || parts[2].length !== 6) {
      setFormErrors({ assessmentId: 'Please enter a valid Assessment ID (XXX/XXX/XXXXXX)' });
      return;
    }
  
    setAssessmentSearchLoading(true);
    setAssessmentSearchError('');
    setSearchMethod('assessment');
    
    // Store search method for session restoration
    sessionStorage.setItem('searchMethod', 'assessment');
  
    try {
      // Use the correct endpoint for searching by assessment reference number
      const assessmentData = await api.get(`/asset/byref`, {
        params: { asst_ref: assessmentId }
      });
  
      if (assessmentData.data?.data) {
        openAssessmentByStatus(assessmentId, assessmentData.data.data);
      } else {
        setAssessmentSearchError('No property details found for this Assessment ID');
      }
    } catch (error) {
      console.error('Search error:', error);
      setAssessmentSearchError('No property details found for this Assessment ID');
    } finally {
      setAssessmentSearchLoading(false);
    }
  };
  
  

// Update the handleWardChange function to call this
const handleWardChange = async (e) => {
  const newWardId = e.target.value;
  setFormData(prev => ({
    ...prev,
    ward_id: newWardId,
    street_id: ''
  }));
  setShowAutocomplete(false);
  
  setAssessmentSearchError('');
  setFormErrors((prev) => ({ ...prev, ward_id: '', assessmentId: '' }));
  
  // Clear previous results when ward changes
  setSearchResults([]);
  setCompletedData([]);
  setAllData([]);
  setHasSearched(false);
  
  // Get selected ward details and update assessment ID with proper format
  const selectedWard = userWards.find(ward => ward.value === newWardId);
  if (selectedWard) {
    // Extract ward number from label (e.g., "Ward 27" → "27")
    const wardNumber = selectedWard.label.match(/\d+/)?.[0];
    if (wardNumber) {
      // Format with leading zeros to make it 3 digits (e.g., "27" → "027")
      const formattedWardNumber = wardNumber.padStart(3, '0');
      // Set assessment ID with org code and ward number (e.g., "009/027/")
      setAssessmentId(`${orgCode}/${formattedWardNumber}/`);
    }
  }

  if (newWardId) {
    setIsInitialLoading(true);
    await loadStreets(newWardId);
    setIsInitialLoading(false);
  } else {
    setUserStreets([]);
  }
};

  const handleStreetChange = (e) => {
    const selectedStreet = e.target.value;
    setFormData(prev => ({ ...prev, street_id: selectedStreet }));
    setAssessmentSearchError('');
    setFormErrors((prev) => ({ ...prev, street_id: '', assessmentId: '' }));
    
    // Explicitly hide the autocomplete dropdown
    setShowAutocomplete(false);
    
    // Only trigger search if a street is selected
    if (selectedStreet) {
      // Process the street data for the selected street
      processStreetData(selectedStreet);
    } else {
      // Clear results if no street is selected
      setSearchResults([]);
      setCompletedData([]);
      setHasSearched(false);
    }
  };

// Replace the processStreetData function
const processStreetData = (selectedStreet = formData.street_id) => {
  if (!selectedStreet || !formData.ward_id) return;
  
  setWardStreetSearchLoading(true);
  setCompletedDataLoading(true);
  
  // Clear any existing search errors
  setSearchError('');

  api.get(`/asset?ward_id=${formData.ward_id}&street_id=${selectedStreet}`)
    .then((response) => {
      const properties = Array.isArray(response.data?.data) ? response.data.data : [];
      const normalizedProperties = properties.map(prop => {
        const status = String(prop.status || '').trim().toLowerCase();
        const isCompleted = status === 'completed' || status === 'complete' ||
          String(prop.completed || '').trim().toLowerCase() === 'yes';
        return {
          id: prop.id,
          door_no: prop.door_no || 'N/A',
          owner: prop.owner || 'N/A',
          asst_ref: prop.asst_ref,
          mobile: prop.mobile || 'N/A',
          eb_no: prop.eb_no || 'N/A',
          street_name: prop.street_name,
          status: prop.status,
          completed: isCompleted
        };
      });

      setAllData(normalizedProperties);
      setSearchResults(normalizedProperties.filter(prop => !prop.completed));
      setCompletedData(normalizedProperties.filter(prop => prop.completed));
      setHasSearched(true);
      setSearchError(normalizedProperties.length === 0 ? 'No Properties Found' : '');

      if (sessionStorage.getItem('fromVerification') === 'true') {
        setActiveTab('pending');
      }
    })
    .catch(error => {
      console.error('Error processing street data:', error);
      setAllData([]);
      setSearchResults([]);
      setCompletedData([]);
      setAllData([]);
      setHasSearched(true);
      setSearchError('Failed to load properties');
    })
    .finally(() => {
      setWardStreetSearchLoading(false);
      setCompletedDataLoading(false);
    });
};
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const fetchWardAssessments = async (wardId) => {
    if (!wardId) return;
    
    try {
      // This is the API call we need to add - adjust the endpoint as needed
      const response = await api.get(`/asset/assessments?ward_id=${wardId}`);
      
      if (response.data?.data) {
        // Store the assessment data for this ward
        setWardAssessments(prev => ({
          ...prev,
          [wardId]: response.data.data
        }));
        
        // Create a lookup for completed assessments
        const completedLookup = {};
        response.data.data.forEach(assessment => {
          if (assessment.status === "completed" || assessment.completed === "Yes") {
            completedLookup[assessment.AssesmentNo] = true;
          }
        });
        
        setCompletedAssessments(completedLookup);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error fetching ward assessments:', error);
      return false;
    }
  };
  // Update the renderTable function to handle different routing
  const renderTable = (data) => (
    <div>
      {/* Desktop Table View */}
      <div className="hidden sm:block w-full">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-100 rounded-lg">
              <th className="text-left font-semibold px-6 py-4 border-b">Assessment ID</th>
              <th className="text-left font-semibold px-6 py-4 border-b">Owner Name</th>
              <th className="text-left font-semibold px-6 py-4 border-b">Mobile</th>
              <th className="text-left font-semibold px-6 py-4 border-b">EB Number</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id} className="border-b hover:bg-gray-50">
                <td className="px-6 py-4 align-middle">
                  <button 
                    onClick={() => storeSessionAndNavigate(item.asst_ref, item.completed, item.id)}
                    className={`${
                      item.completed 
                        ? 'text-green-600 hover:text-green-700' 
                        : 'text-[#75d1e3] hover:text-[#5dbdd0]'
                    } hover:underline cursor-pointer`}
                  >
                    {item.asst_ref}
                  </button>
                </td>
                <td className="px-6 py-4 align-middle">{item.owner}</td>
                <td className="px-6 py-4 align-middle">{item.mobile || 'N/A'}</td>
                <td className="px-6 py-4 align-middle">{item.eb_no || 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
  
      {/* Mobile Card View */}
      <div className="sm:hidden">
        <div className="space-y-4">
          {data.map((item) => (
            <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Assessment ID</p>
                    <button 
                      onClick={() => storeSessionAndNavigate(item.asst_ref, item.completed, item.id)}
                      className={`${
                        item.completed 
                          ? 'text-green-600 hover:text-green-700' 
                          : 'text-[#75d1e3] hover:text-[#5dbdd0]'
                      } hover:underline text-lg font-medium`}
                    >
                      {item.asst_ref}
                    </button>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500 mb-1">Owner Name</p>
                    <p className="text-gray-900">{item.owner}</p>
                  </div>
                </div>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Mobile</p>
                    <p className="text-gray-900">{item.mobile || 'N/A'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500 mb-1">EB Number</p>
                    <p className="text-gray-900">{item.eb_no || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  
  
  const handleAutocompleteSelection = async (selectedAssessmentNo) => {
    setAssessmentId(selectedAssessmentNo);
    setShowAutocomplete(false);
    setAssessmentSearchLoading(true);
  
    try {
      const assessmentData = await api.get('/asset/byref', {
        params: { asst_ref: selectedAssessmentNo }
      });
      if (!openAssessmentByStatus(selectedAssessmentNo, assessmentData.data?.data)) {
        setAssessmentSearchError('No property details found for this Assessment ID');
      }
    } catch (error) {
      console.error('Error:', error);
      setAssessmentSearchError('Error processing selection');
    } finally {
      setAssessmentSearchLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white shadow-xl rounded-2xl border border-gray-100">
          {/* Header Section */}
          <div className="p-4 lg:p-6">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex flex-col lg:flex-row items-start lg:items-end gap-4 lg:gap-6 w-full lg:w-auto">
              {/* Ward Selection */}
              <div className="w-full lg:w-48">
                <label htmlFor="ward_id" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Ward Name
                </label>
                <select
                  id="ward_id"
                  value={formData.ward_id}
                  onChange={handleWardChange}
                  className="w-full lg:w-48 px-3 py-2 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#75d1e3] focus:border-[#75d1e3] transition-all duration-200 hover:border-[#75d1e3]"
                >
                  <option value="">Select Ward</option>
                  {userWards.map((ward, index) => (
                    <option key={`${ward.value}-${index}`} value={ward.value}>
                      {ward.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* OR Divider */}
              <div className="flex items-center justify-center w-full lg:w-auto lg:h-11">
                <span className="text-gray-500 font-medium">OR</span>
              </div>

              {/* Assessment Number */}
              <div className="w-full lg:w-64">
                <label htmlFor="assessment_id" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Enter Assessment Number
                </label>
                <div className="relative">
                  <input
                    ref={assessmentInputRef}
                    type="text"
                    id="assessment_id"
                    value={assessmentId}
                    onChange={handleAssessmentIdChange}
                    className={`w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#75d1e3] focus:border-[#75d1e3] transition-all duration-200
                      ${!formData.ward_id ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-[#75d1e3]'}`}
                    placeholder={orgCode ? `${orgCode}/024/111111` : "111/111/111111"}
                    disabled={isInitialLoading || !formData.ward_id}
                  />
                  {(isInitialLoading || autocompleteLoading) && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#75d1e3]"></div>
                    </div>
                  )}
                  {showAutocomplete && !isInitialLoading && (
                    <div ref={autocompleteRef} className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg">
                      {autocompleteResults.length > 0 ? (
                        <ul className="py-1 max-h-60 overflow-auto">
                          {autocompleteResults.map((result, index) => (
                            <li
                              key={index}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${completedAssessments[result] ? 'text-green-600 font-medium' : ''}`}
                              onClick={() => handleAutocompleteSelection(result)}
                            >
                              {result}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="p-4 text-center text-gray-500">No matching assessment numbers found</div>
                      )}
                    </div>
                  )}
                </div>
                {formErrors.assessmentId && (
                  <p className="mt-1 text-red-500 text-sm flex items-center gap-1">
                    <AlertCircle size={14} />
                    {formErrors.assessmentId}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 w-full lg:w-auto lg:pb-2">
              <div className="p-1.5 bg-[#75d1e3]/10 rounded-lg">
                <Building className="h-4 w-4 text-[#75d1e3]" />
              </div>
              <p className="text-sm font-semibold text-gray-700 text-right">
                {orgName} <span className="mx-1 text-gray-400">&gt;&gt;</span> {zoneName}
              </p>
            </div>
            </div>
          </div>
          <div className="px-4 pb-4 lg:px-6 lg:pb-6">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-4">
              {/* Street Selection */}
              <div className="w-full lg:w-[21.12rem]">
                <label htmlFor="street_id" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Street Name
                </label>
                <div className="relative">
                  <Home className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${!formData.ward_id ? 'text-gray-400' : 'text-[#75d1e3]'}`} />
                  <select
                    id="street_id"
                    value={formData.street_id}
                    onChange={handleStreetChange}
                    className={`w-full pl-10 pr-10 py-3.5 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#75d1e3] focus:border-[#75d1e3] transition-all duration-200 appearance-none
                      ${!formData.ward_id ? 'bg-gray-100 cursor-not-allowed border-gray-200' : 'hover:border-[#75d1e3]'}`}
                    disabled={!formData.ward_id || streetLoading}
                  >
                    <option value="">Select Street</option>
                    {userStreets.map((street, index) => (
                      <option key={`${street.value}-${index}`} value={street.value}>
                        {street.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 ${!formData.ward_id ? 'text-gray-400' : 'text-gray-400'}`} />
                </div>
                {(streetError || formErrors.street_id) && (
                  <p className="mt-1 text-red-500 text-sm flex items-center gap-1">
                    <AlertCircle size={14} />
                    <span>{streetError || formErrors.street_id}</span>
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 text-right lg:pb-3.5">
                <Info size={18} className="shrink-0 text-[#75d1e3]" />
                <span className="text-sm text-gray-600">
                  Assessment numbers in <span className="font-medium text-green-600">green</span> indicate completed surveys.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Error Messages */}
        {assessmentSearchError && (
          <div className="bg-white shadow-md rounded-lg mt-6 mb-8">
            <div className="p-6 text-center text-red-500 flex items-center justify-center gap-2">
              <AlertCircle size={20} />
              <span className="font-medium">{assessmentSearchError}</span>
            </div>
          </div>
        )}

        {/* Results Section */}
        {((searchResults.length > 0 || completedData.length > 0) || (searchError && hasSearched)) && (
          <div className="bg-white shadow-md rounded-lg mt-6 mb-8">
            <div className="border-b border-gray-200">
              <div className="flex flex-wrap">
                <Tab 
                  active={activeTab === 'pending'} 
                  icon={ClipboardList}
                  label="Pending"
                  onClick={() => handleTabChange('pending')}
                />
                <Tab 
                  active={activeTab === 'completed'} 
                  icon={CheckCircle}
                  label="Completed"
                  onClick={() => handleTabChange('completed')}
                />
                <Tab
                  active={activeTab === 'all'}
                  icon={ClipboardCheck}
                  label="All"
                  onClick={() => handleTabChange('all')}
                />
              </div>
            </div>
            {searchError && hasSearched ? (
              <div className="p-6 text-center text-red-500">
                {searchError}
              </div>
            ) : (
              <div className="sm:overflow-x-auto">
                <div className="sm:min-w-[800px] p-4 sm:p-6">
                  {activeTab === 'pending' ? (
                    wardStreetSearchLoading ? (
                      <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#75d1e3]"></div>
                      </div>
                    ) : (
                      searchResults.length > 0 ? (
                        renderTable(searchResults)
                      ) : (
                        <div className="p-6 text-center text-gray-600">
                          No pending properties found for the selected criteria.
                        </div>
                      )
                    )
                  ) : activeTab === 'completed' ? (
                    completedDataLoading ? (
                      <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#75d1e3]"></div>
                      </div>
                    ) : (
                      completedData.length > 0 ? (
                        renderTable(completedData)
                      ) : (
                        <div className="p-6 text-center text-gray-600">
                          No completed properties found for the selected criteria.
                        </div>
                      )
                    )
                  ) : (
                    allData.length > 0 ? renderTable(allData) : (
                      <div className="p-6 text-center text-gray-600">No properties found for the selected criteria.</div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyForm;
